"use client";

import { useEffect, useRef, useState, useTransition } from "react";
import { useLocale, useTranslations } from "next-intl";
import { ChevronDownIcon, CheckIcon } from "@heroicons/react/24/solid";
import { usePathname, useRouter, routing } from "@/i18n/routing";
import type { Locale } from "@/i18n/routing";

type Opt = {
  /** locale code used by next-intl routing */
  code: Locale;
  /** ISO-3166 alpha-2 country code for flag-icons (lowercase) */
  iso: string;
  /** native language name shown in the dropdown */
  native: string;
};

const options: Opt[] = [
  { code: "de", iso: "de", native: "Deutsch" },
  { code: "fr", iso: "fr", native: "Français" },
  { code: "nl", iso: "nl", native: "Nederlands" },
  { code: "it", iso: "it", native: "Italiano" },
  { code: "pl", iso: "pl", native: "Polski" },
  { code: "en", iso: "gb", native: "English" },
  { code: "es", iso: "es", native: "Español" },
  { code: "cs", iso: "cz", native: "Čeština" },
  { code: "zh", iso: "cn", native: "中文 (中国)" },
  { code: "da", iso: "dk", native: "Dansk" },
  { code: "sv", iso: "se", native: "Svenska" },
  { code: "hu", iso: "hu", native: "Magyar" },
  { code: "el", iso: "gr", native: "Ελληνικά" },
  { code: "tr", iso: "tr", native: "Türkçe" },
  { code: "fi", iso: "fi", native: "Suomi" },
  { code: "no", iso: "no", native: "Norsk" },
  { code: "pt", iso: "pt", native: "Português" },
];

export function LanguageSwitcher({
  compact = false,
  native = false,
}: {
  compact?: boolean;
  /** Render a native <select> (uses the OS picker — best UX on mobile). */
  native?: boolean;
}) {
  const t = useTranslations("Common");
  const locale = useLocale() as Locale;
  const router = useRouter();
  const pathname = usePathname();
  const [, start] = useTransition();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const current = options.find((o) => o.code === locale) ?? options[0];

  useEffect(() => {
    if (!open || native) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, native]);

  function pick(code: Locale) {
    if (code === locale) {
      setOpen(false);
      return;
    }
    start(() =>
      router.replace(pathname, { locale: code as (typeof routing.locales)[number] }),
    );
    setOpen(false);
  }

  if (native) {
    return (
      <label className="relative block w-full">
        <span className="sr-only">{t("languageLabel")}</span>
        <span className="pointer-events-none flex h-11 items-center gap-2.5 rounded-md border border-line-200 bg-white px-3 text-sm">
          <span
            aria-hidden
            className={`fi fi-${current.iso} h-3 w-[1.125rem] !rounded-[2px]`}
          />
          <span className="font-medium text-ink-900">{current.native}</span>
          <ChevronDownIcon className="ml-auto h-4 w-4 text-ink-500" aria-hidden />
        </span>
        <select
          value={locale}
          onChange={(e) => pick(e.target.value as Locale)}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          aria-label={t("languageLabel")}
        >
          {options.map((o) => (
            <option key={o.code} value={o.code}>
              {o.native}
            </option>
          ))}
        </select>
      </label>
    );
  }

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        // Accessible name starts with the visible locale code (e.g. "DE")
        // so axe-core's label-content-name-mismatch is satisfied.
        aria-label={`${current.code.toUpperCase()} – ${t("languageLabel")}`}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 rounded-md px-2 py-1 text-xs font-medium text-ink-700 transition-colors hover:bg-soft hover:text-ink-900"
      >
        <span
          aria-hidden
          className={`fi fi-${current.iso} h-3 w-[1.125rem] !rounded-[2px]`}
        />
        {!compact ? (
          <span className="font-semibold tracking-wide">{current.code.toUpperCase()}</span>
        ) : null}
        <ChevronDownIcon className="h-3 w-3 text-ink-500" aria-hidden />
      </button>

      {open ? (
        <ul
          role="listbox"
          aria-label={t("languageLabel")}
          // 2-column grid + viewport-bounded scroll: 17 locales never run past
          // the screen edge on short/landscape viewports — the panel caps at
          // 70vh and scrolls internally instead of clipping.
          className="absolute right-0 top-full z-50 mt-2 grid max-h-[min(70vh,22rem)] w-[min(90vw,24rem)] grid-cols-2 gap-0.5 overflow-y-auto overscroll-contain rounded-md border border-line-200 bg-white p-1 shadow-lg"
        >
          {options.map((o) => {
            const active = o.code === locale;
            return (
              <li key={o.code} role="option" aria-selected={active}>
                <button
                  type="button"
                  onClick={() => pick(o.code)}
                  className={`flex w-full items-center gap-2 rounded px-2.5 py-2 text-left text-sm transition-colors hover:bg-soft ${active ? "bg-soft/60" : ""}`}
                >
                  <span
                    aria-hidden
                    className={`fi fi-${o.iso} h-3.5 w-[1.375rem] shrink-0 !rounded-[2px]`}
                  />
                  <span className="truncate font-medium text-ink-900">{o.native}</span>
                  {active ? (
                    <CheckIcon className="ml-auto h-4 w-4 shrink-0 text-ink-700" aria-hidden />
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
